package dev.lingvo.site.components.editor

import androidx.compose.runtime.mutableStateMapOf
import dev.lingvo.shared.CommentDto
import dev.lingvo.shared.UpdateCommentRequest
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionRequestIdentity
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import dev.lingvo.site.i18n.t

/** A failed write is uncertain until a deliberate read confirms the current discussion. */
internal class EditorCommentResolutionStore(
    private val projectSlug: String,
    private val scope: CoroutineScope,
    private val canWrite: () -> Boolean,
    private val currentKeyId: () -> String?,
    private val comments: MutableMap<String, Async<List<CommentDto>>>,
) {
    val busy = mutableStateMapOf<String, Boolean>()
    val problems = mutableStateMapOf<String, String>()
    val reloading = mutableStateMapOf<String, Boolean>()

    fun update(comment: CommentDto) {
        val keyId = comment.keyId
        val actor = Session.requestIdentity ?: return
        if (!owns(actor, keyId) || !canWrite() || busy[comment.id] == true || problems[keyId] != null || reloading[keyId] == true) return
        val current = (comments[keyId] as? Async.Success)?.value?.firstOrNull { it.id == comment.id } ?: return
        if (current != comment) return
        val resolved = !comment.resolved
        busy[comment.id] = true
        scope.launch {
            try {
                ApiClient.updateKeyComment(projectSlug, keyId, comment.id, UpdateCommentRequest(resolved))
                if (owns(actor, keyId) && canWrite()) {
                    val loaded = comments[keyId] as? Async.Success
                    if (loaded != null) comments[keyId] = Async.Success(loaded.value.map { if (it.id == comment.id) it.copy(resolved = resolved) else it })
                }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Throwable) {
                if (owns(actor, keyId)) problems[keyId] = tr(
                    "editor.editor_comment_resolution_store.the_discussion_change_could_not_be",
                    "The discussion change could not be confirmed. Reload the discussion before trying again.",
                )
            } finally {
                busy.remove(comment.id)
            }
        }
    }

    fun reload(keyId: String) {
        val actor = Session.requestIdentity ?: return
        if (!owns(actor, keyId) || isBusy(keyId) || reloading[keyId] == true || problems[keyId] == null) return
        reloading[keyId] = true
        scope.launch {
            try {
                val loaded = ApiClient.listKeyComments(projectSlug, keyId)
                if (owns(actor, keyId)) {
                    comments[keyId] = Async.Success(loaded)
                    problems.remove(keyId)
                }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Throwable) {
                if (owns(actor, keyId)) problems[keyId] = tr(
                    "editor.editor_comment_resolution_store.couldnt_reload_the_discussion_check_your",
                    "Couldn't reload the discussion. Check your connection and try again.",
                )
            } finally {
                reloading.remove(keyId)
            }
        }
    }

    private fun owns(actor: SessionRequestIdentity, keyId: String): Boolean =
        Session.requestIdentity == actor && currentKeyId() == keyId

    fun isBusy(keyId: String): Boolean =
        (comments[keyId] as? Async.Success)?.value.orEmpty().any { busy[it.id] == true }
}
